import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import moment from "moment";
import { Operationstyper } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { SetProduktionsdatumCommand } from "../commands/SetProduktionsdatumCommand";
import { ArbetsorderService } from "../services/arbetsorder.service";
import { PlaneraService } from "../services/planera.service";

@Component({
    selector: "rask-arbetsorder-produktionsdatum",
    templateUrl: "./arbetsorder-produktionsdatum.component.html",
    styleUrls: ["./arbetsorder-produktionsdatum.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DatepickerComponent,
        FormsModule,
        ButtonComponent
    ]
})
export class ArbetsorderProduktionsdatumComponent implements OnChanges {
    @Input() arbetsorder: IArbetsorderDetail;
    @Input() onProduktionsdatumUpdated: () => void;
    slutdatum: string;
    startdatum: string;

    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly planeraService = inject(PlaneraService);

    getSuggestion(): void {
        this.planeraService.getForeslagetProduktionsdatum(this.arbetsorder.id)
            .subscribe((response) => {
                if (!response || !response.startdatum || !response.slutdatum) {
                    return;
                }
                this.startdatum = moment(response.startdatum).format();
                this.slutdatum = moment(response.slutdatum).format();

                if (this.operationChanged()) {
                    this.addOrUppdateCommand(this.startdatum, this.slutdatum);
                    this.onProduktionsdatumUpdated();
                }
            });
    }

    isTerminalImportOrExternArbetsorder(): boolean {
        return this.arbetsorder.isTerminalImport || this.arbetsorder.isExternArbetsorder;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.arbetsorder || !this.arbetsorder.operationer) {
            return;
        }

        const skotningOperation = this.arbetsorderService
            .getOperationByOperationstyp(this.arbetsorder, Operationstyper.Skotning);
        if (!skotningOperation || !skotningOperation.startdatum || !skotningOperation.slutdatum) {
            return;
        }

        this.startdatum = skotningOperation.startdatum;
        this.slutdatum = moment(skotningOperation.slutdatum)
            .subtract(1, "days")
            .format();
    }

    onCalendarChange($event): void {
        if (this.operationChanged()) {
            const startdatumString = !ObjectUtils.isNullOrUndefined(this.startdatum) ? moment(this.startdatum).format() : undefined;
            const slutdatumString = !ObjectUtils.isNullOrUndefined(this.slutdatum) ? moment(this.slutdatum).add(1, "days").format() : undefined;
            this.addOrUppdateCommand(startdatumString, slutdatumString);
            this.onProduktionsdatumUpdated();
        }
    }

    private addOrUppdateCommand(startdatum: string, slutdatum: string) {
        this.arbetsorder.commandManager.addOrUpdate(
            new SetProduktionsdatumCommand(command => {
                command.arbetsorderId = this.arbetsorder.id;
                command.startdatum = startdatum;
                command.slutdatum = slutdatum;
            }),
            (command) =>
                command.$type === SetProduktionsdatumCommand.$type &&
                command.arbetsorderId === this.arbetsorder.id
        );
    }

    private isInputAValidDate(input: string): boolean {
        return input && moment(input).isValid();
    }

    private isSlutdatumAValidDate(): boolean {
        return ObjectUtils.isNullOrUndefined(this.slutdatum) || this.isInputAValidDate(this.slutdatum);
    }

    private isStartdatumAValidDate(): boolean {
        return ObjectUtils.isNullOrUndefined(this.startdatum) || this.isInputAValidDate(this.startdatum);
    }

    private operationChanged(): boolean {
        if(!this.isStartdatumAValidDate() || !this.isSlutdatumAValidDate()) {
            return false;
        }
        const skotningOperation = this.arbetsorderService.getOperationByOperationstyp(
            this.arbetsorder,
            Operationstyper.Skotning);
        const startdatumString = moment(this.startdatum)
            .format();
        const slutdatumString = moment(this.slutdatum)
            .add(1, "days")
            .format();
        if (skotningOperation) {
            return skotningOperation.startdatum !== startdatumString || skotningOperation.slutdatum !== slutdatumString;
        }
        return true;
    }
}
